import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, TruckIcon, CalendarDays, FileSpreadsheet, Settings2, RotateCcw } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import api from "../api";

const fmtRp = (v) => `Rp ${Math.round(v || 0).toLocaleString("id-ID")}`;
const fmtKg = (v) => `${Number(v || 0).toLocaleString("id-ID", { maximumFractionDigits: 2 })} KG`;
const fmtTgl = (v) => {
  const d = new Date(v + "T00:00:00");
  return isNaN(d) ? v : d.toLocaleDateString("id-ID", { day: "2-digit", month: "short" });
};
const bulanIni = () => {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}`;
};

const KomoditasCards = ({ rows, aktif, onPilih, mode }) => {
  if (!rows?.length) return null;
  return (
    <div data-testid={`rekap-komoditas-cards-${mode}`} className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {rows.map((r) => {
        const on = aktif === r.komoditas;
        return (
          <button key={r.komoditas} type="button" onClick={() => onPilih(on ? "" : r.komoditas)}
            data-testid={`rekap-komoditas-card-${mode}-${r.komoditas.replace(/ /g, "-")}`}
            className={`text-left rounded-md border px-4 py-3 transition-colors ${on ? "border-emerald-600 bg-emerald-50 ring-1 ring-emerald-600" : "border-zinc-200 bg-white hover:bg-zinc-50"}`}>
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-zinc-900">{r.komoditas}</span>
              <span className="text-xs text-zinc-500">{r.trip} trip</span>
            </div>
            <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
              <div><p className="text-zinc-500">Tonase</p><p className="font-medium tabular-nums text-zinc-800">{fmtKg(r.tonase)}</p></div>
              <div><p className="text-zinc-500">Pendapatan</p><p className="font-medium tabular-nums text-zinc-800">{fmtRp(r.pendapatan)}</p></div>
              <div><p className="text-zinc-500">Laba</p><p className={`font-semibold tabular-nums ${r.laba >= 0 ? "text-emerald-800" : "text-red-700"}`}>{fmtRp(r.laba)}</p></div>
            </div>
          </button>
        );
      })}
    </div>
  );
};


const TarifDialog = ({ open, onClose, mode, tarif, onSaved }) => {
  const [vals, setVals] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && tarif) setVals(Object.fromEntries(Object.entries(tarif).map(([k, v]) => [k, String(v)])));
  }, [open, tarif]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/laporan/tarif", {
        mode,
        tarif: Object.fromEntries(Object.entries(vals).map(([k, v]) => [k, parseFloat(v)])),
      });
      toast.success("Tarif ongkos angkut diperbarui");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Gagal menyimpan tarif");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid={`tarif-dialog-${mode}`} className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Ubah Ongkos Angkut {mode === "owner" ? "(Tarif Pemilik)" : "(Tarif Standar)"}</DialogTitle>
          <DialogDescription>Tarif per KG untuk setiap komoditas. Perubahan langsung berlaku pada perhitungan.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {Object.keys(vals).map((k) => (
            <div key={k} className="space-y-1.5">
              <Label>{k} (Rp/KG)</Label>
              <Input data-testid={`tarif-input-${mode}-${k.replace(/ /g, "-")}`} type="number" min="1" step="0.01"
                value={vals[k]} onChange={(e) => setVals((p) => ({ ...p, [k]: e.target.value }))} required />
            </div>
          ))}
          <Button data-testid={`tarif-submit-${mode}`} type="submit" disabled={saving}
            className="w-full bg-emerald-700 hover:bg-emerald-800 text-white">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simpan Tarif"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const RekapKendaraan = ({ mode = "standar", title = "Ringkasan Kendaraan Bulanan" }) => {
  const storeKey = `rekap-kendaraan-${mode}`;
  const saved = (() => { try { return JSON.parse(localStorage.getItem(storeKey) || "{}"); } catch { return {}; } })();
  const [bulan, setBulan] = useState(saved.bulan || null);
  const [komoditas, setKomoditas] = useState(saved.komoditas || "");
  const [data, setData] = useState(null);
  const [tarifOpen, setTarifOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const qs = `bulan=${bulan}&mode=${mode}${komoditas ? `&komoditas=${encodeURIComponent(komoditas)}` : ""}`;

  useEffect(() => {
    if (bulan) return;
    api.get("/laporan/bulan-terakhir")
      .then(({ data }) => setBulan(data.bulan || bulanIni()))
      .catch(() => setBulan(bulanIni()));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!bulan) return;
    localStorage.setItem(storeKey, JSON.stringify({ bulan, komoditas }));
    setData(null);
    api.get(`/laporan/rekap-kendaraan?${qs}`)
      .then(({ data }) => setData(data))
      .catch(() => setData({ kendaraan: [], grand_total: {}, tarif: {}, catatan: "" }));
  }, [bulan, mode, komoditas, reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const resetFilter = async () => {
    setKomoditas("");
    try {
      const { data } = await api.get("/laporan/bulan-terakhir");
      setBulan(data.bulan || bulanIni());
    } catch {
      setBulan(bulanIni());
    }
  };

  const exportExcel = async () => {
    try {
      const res = await api.get(`/laporan/rekap-kendaraan/export?${qs}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Rekap_Kendaraan_${mode === "owner" ? "Pemilik_" : ""}${bulan}${komoditas ? `_${komoditas.replace(/ /g, "")}` : ""}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Rekap kendaraan diekspor ke Excel");
    } catch (err) {
      toast.error("Gagal mengekspor rekap");
    }
  };

  return (
    <Card className="border-zinc-200 shadow-sm" data-testid={`rekap-kendaraan-${mode}`}>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle className="text-base font-semibold tracking-tight flex items-center gap-2">
            <TruckIcon className="w-4 h-4 text-emerald-700" /> {title}
          </CardTitle>
          <div className="flex items-center gap-2 flex-wrap">
            <CalendarDays className="w-4 h-4 text-zinc-400" />
            <Input data-testid={`rekap-kendaraan-bulan-${mode}`} type="month" value={bulan || bulanIni()}
              onChange={(e) => e.target.value && setBulan(e.target.value)} className="h-8 w-44 bg-white" />
            <select data-testid={`rekap-kendaraan-komoditas-${mode}`} value={komoditas} onChange={(e) => setKomoditas(e.target.value)}
              className="h-8 rounded-md border border-zinc-300 bg-white px-2 text-sm text-zinc-800">
              <option value="">Semua Komoditas</option>
              {Object.keys(data?.tarif || { CPO: 1, "Palm Kernel": 1, Cangkang: 1 }).map((k) => (
                <option key={k} value={k}>{k}</option>
              ))}
            </select>
            <Button data-testid={`rekap-kendaraan-reset-${mode}`} size="sm" variant="ghost" onClick={resetFilter}
              title="Kembali ke bulan berdata terakhir & semua komoditas"
              className="h-8 px-2 text-zinc-600 hover:text-emerald-800 hover:bg-emerald-50">
              <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset
            </Button>
            <Button data-testid={`rekap-kendaraan-export-${mode}`} size="sm" variant="outline" onClick={exportExcel}
              className="h-8 border-emerald-300 text-emerald-800 hover:bg-emerald-50">
              <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5" /> Excel
            </Button>
            <Button data-testid={`rekap-kendaraan-tarif-${mode}`} size="sm" variant="outline" onClick={() => setTarifOpen(true)}
              className="h-8 border-zinc-300 text-zinc-700 hover:bg-zinc-50">
              <Settings2 className="w-3.5 h-3.5 mr-1.5" /> Ubah Tarif
            </Button>
          </div>
        </div>
        {data && data.tarif && (
          <p className="text-xs text-zinc-500">
            Ongkos angkut: {Object.entries(data.tarif).map(([k, v]) => `${k} Rp ${Number(v).toLocaleString("id-ID")}/KG`).join(" • ")}
          </p>
        )}
      </CardHeader>
      <CardContent className="space-y-5">
        {data === null ? (
          <div className="flex justify-center py-10 text-zinc-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
        ) : (
          <>
          <KomoditasCards rows={data.per_komoditas} aktif={komoditas} onPilih={setKomoditas} mode={mode} />
          {data.kendaraan.length === 0 ? (
          <p data-testid={`rekap-kendaraan-empty-${mode}`} className="py-6 text-center text-sm text-zinc-400">
            Tidak ada trip{komoditas ? ` komoditas ${komoditas}` : ""} pada bulan ini.
          </p>
        ) : (
          <>
            {data.kendaraan.map((k) => (
              <div key={k.plat} data-testid={`rekap-kendaraan-${mode}-${k.plat.replace(/ /g, "-")}`}
                className="border border-zinc-200 rounded-md overflow-hidden">
                <div className="px-4 py-2.5 bg-zinc-50 border-b border-zinc-200 flex items-center justify-between">
                  <span className="text-sm font-semibold text-zinc-900">{k.plat}</span>
                  <span className={`text-sm font-semibold tabular-nums ${k.total.laba >= 0 ? "text-emerald-800" : "text-red-700"}`}>
                    Laba: {fmtRp(k.total.laba)}
                  </span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-zinc-200 bg-white">
                        {["Tanggal", "Komoditas", "Trip", "Tonase", "Susut (KG)", "Pendapatan", "Biaya Operasional", "Kerugian Susut", "Laba Harian"].map((h) => (
                          <th key={h} className="text-left px-4 py-2 text-xs uppercase tracking-wider text-zinc-500 font-medium whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {k.harian.map((r) => (
                        <tr key={`${r.tanggal}-${r.komoditas}`} className="border-b border-zinc-100 hover:bg-zinc-50">
                          <td className="px-4 py-2 whitespace-nowrap">{fmtTgl(r.tanggal)}</td>
                          <td data-testid={`rekap-komoditas-${mode}-${k.plat.replace(/ /g, "-")}-${r.tanggal}`} className="px-4 py-2 whitespace-nowrap">
                            {r.komoditas && r.komoditas !== "-" ? (
                              <span className="px-1.5 py-0.5 rounded text-xs bg-amber-50 text-amber-800 border border-amber-200">{r.komoditas}</span>
                            ) : <span className="text-zinc-400">-</span>}
                          </td>
                          <td className="px-4 py-2 tabular-nums">{r.trip}</td>
                          <td className="px-4 py-2 tabular-nums whitespace-nowrap">{fmtKg(r.tonase)}</td>
                          <td className={`px-4 py-2 tabular-nums whitespace-nowrap ${r.susut > 0 ? "text-emerald-700" : r.susut < 0 ? "text-red-700" : "text-zinc-400"}`}>{fmtKg(r.susut)}</td>
                          <td className="px-4 py-2 tabular-nums whitespace-nowrap">{fmtRp(r.pendapatan)}</td>
                          <td className="px-4 py-2 tabular-nums whitespace-nowrap text-red-700">{fmtRp(r.biaya)}</td>
                          <td className={`px-4 py-2 tabular-nums whitespace-nowrap ${r.rugi_susut > 0 ? "text-red-700" : "text-zinc-400"}`}>{fmtRp(r.rugi_susut)}</td>
                          <td className={`px-4 py-2 tabular-nums whitespace-nowrap font-medium ${r.laba >= 0 ? "text-emerald-800" : "text-red-700"}`}>{fmtRp(r.laba)}</td>
                        </tr>
                      ))}
                      <tr className="bg-emerald-50 font-semibold">
                        <td className="px-4 py-2">Total Bulan</td>
                        <td className="px-4 py-2" />
                        <td className="px-4 py-2 tabular-nums">{k.total.trip}</td>
                        <td className="px-4 py-2 tabular-nums whitespace-nowrap">{fmtKg(k.total.tonase)}</td>
                        <td className={`px-4 py-2 tabular-nums whitespace-nowrap ${k.total.susut > 0 ? "text-emerald-700" : k.total.susut < 0 ? "text-red-700" : ""}`}>{fmtKg(k.total.susut)}</td>
                        <td className="px-4 py-2 tabular-nums whitespace-nowrap">{fmtRp(k.total.pendapatan)}</td>
                        <td className="px-4 py-2 tabular-nums whitespace-nowrap text-red-700">{fmtRp(k.total.biaya)}</td>
                        <td className={`px-4 py-2 tabular-nums whitespace-nowrap ${k.total.rugi_susut > 0 ? "text-red-700" : ""}`}>{fmtRp(k.total.rugi_susut)}</td>
                        <td className={`px-4 py-2 tabular-nums whitespace-nowrap ${k.total.laba >= 0 ? "text-emerald-800" : "text-red-700"}`}>{fmtRp(k.total.laba)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
            <div data-testid={`rekap-kendaraan-grand-${mode}`}
              className="flex flex-wrap items-center justify-between gap-2 bg-emerald-700 text-white rounded-md px-4 py-3">
              <span className="text-sm font-semibold">
                TOTAL SEMUA KENDARAAN — {data.grand_total.trip} trip, {fmtKg(data.grand_total.tonase)}, susut {fmtKg(data.grand_total.susut)}
              </span>
              <span className="text-sm font-semibold tabular-nums">
                Pendapatan {fmtRp(data.grand_total.pendapatan)} − Biaya {fmtRp(data.grand_total.biaya)} − Rugi Susut {fmtRp(data.grand_total.rugi_susut)} = Laba {fmtRp(data.grand_total.laba)}
              </span>
            </div>
        {data && data.catatan && <p className="text-xs text-zinc-500">• {data.catatan}</p>}
          </>
        )}
          </>
        )}
      </CardContent>
      <TarifDialog open={tarifOpen} onClose={() => setTarifOpen(false)} mode={mode}
        tarif={data?.tarif} onSaved={() => setReloadKey((k) => k + 1)} />
    </Card>
  );
};
